import React, { useState, useEffect } from 'react'
import { useShallow } from 'zustand/react/shallow'
import { useFlagStore, selectAllFlags, selectRequirementFlags } from 'stores/flagStore'
import { usePackageStore } from 'stores/packageStore'
import { FlagItem } from './FlagItem'

const SCROLL_OFFSET = 150

function scrollToRequirement(requirementId) {
  const el = document.querySelector(`.list-group-item-${requirementId}`)
  if (!el) return
  const top = el.getBoundingClientRect().top + window.scrollY - SCROLL_OFFSET
  window.scrollTo({ top, behavior: 'smooth' })
}

// TODO: reconcile with APPLICANT_ROLES in ./constants/index.js — that list has 'broker' instead of 'applicant'
const APPLICANT_ROLES = ['applicant', 'client', 'resident', 'spectator']

function isApplicant(role) {
  return APPLICANT_ROLES.includes(role)
}

function filterForReviewing(flags, role) {
  if (isApplicant(role)) return flags.filter((f) => ['reported', 'not_accepted'].includes(f.state))
  return flags.filter((f) => ['drafted', 'reported', 'fixed', 'not_accepted'].includes(f.state))
}

function filterForReviewed(flags, role) {
  if (isApplicant(role)) return flags.filter((f) => ['fixed', 'accepted'].includes(f.state))
  return flags.filter((f) => f.state === 'accepted')
}

function sortByCreatedAt(flags) {
  return [...flags].sort((a, b) => {
    const ta = a.created_at ? new Date(a.created_at).getTime() : a.id
    const tb = b.created_at ? new Date(b.created_at).getTime() : b.id
    return ta - tb
  })
}

function RequirementRow({ requirement, index, role, tab, isOpen, isSelected, onSelect }) {
  const documents = usePackageStore(
    useShallow(
      (s) => s.package?.visible_package_requirements?.find((r) => r.id === requirement.id)?.package_documents ?? []
    )
  )

  const reqFlags = useFlagStore(useShallow(selectRequirementFlags(requirement.id)))
  const allDocFlags = useFlagStore(useShallow((s) => documents.flatMap((doc) => s.documentFlags[doc.id] ?? [])))

  const filteredReqFlags = sortByCreatedAt(
    tab === 'reviewing' ? filterForReviewing(reqFlags, role) : filterForReviewed(reqFlags, role)
  )

  const filteredDocGroups = documents
    .map((doc) => ({
      doc,
      flags: sortByCreatedAt(
        tab === 'reviewing'
          ? filterForReviewing(
              allDocFlags.filter((f) => f.flaggable_id === doc.id),
              role
            )
          : filterForReviewed(
              allDocFlags.filter((f) => f.flaggable_id === doc.id),
              role
            )
      ),
    }))
    .filter(({ flags }) => flags.length > 0)

  const totalFlagCount = filteredReqFlags.length + filteredDocGroups.reduce((sum, { flags }) => sum + flags.length, 0)
  const hasFlags = totalFlagCount > 0
  const [expanded, setExpanded] = useState(true)
  const isReviewing = tab === 'reviewing'

  useEffect(() => {
    if (isOpen) setExpanded(true)
  }, [isOpen])

  function selectAndScroll() {
    onSelect()
    scrollToRequirement(requirement.id)
  }

  function handleCardClick() {
    if (isReviewing) selectAndScroll()
  }

  function handleSelectClick(e) {
    e.stopPropagation()
    if (isReviewing) {
      selectAndScroll()
    } else if (hasFlags) {
      setExpanded((v) => !v)
    }
  }

  function handleChevronClick(e) {
    e.stopPropagation()
    setExpanded((v) => !v)
  }

  return (
    <div
      className={`flags-requirement${hasFlags ? ' has-flags' : ''}${isReviewing ? ' is-reviewing' : ''}${
        isReviewing && isSelected ? ' is-selected' : ''
      }`}
      onClick={isReviewing ? handleCardClick : undefined}
    >
      <div className="requirement-header dome-d-flex dome-align-center dome-justify-start dome-gap3 dome-p1">
        <button
          type="button"
          className="requirement-select-btn dome-d-flex dome-align-center dome-gap3"
          onClick={handleSelectClick}
          disabled={!isReviewing && !hasFlags}
        >
          <span className="requirement-number">{index + 1}.</span>
          <span className="requirement-title">{requirement.title}</span>
          {hasFlags && (
            <span className="requirement-count dome-color-aqua dome-text-w500">({totalFlagCount})</span>
          )}
        </button>
        {hasFlags && (
          <button
            type="button"
            className="requirement-chevron-btn"
            onClick={handleChevronClick}
            aria-expanded={expanded}
            aria-label={expanded ? 'collapse requirement flags' : 'expand requirement flags'}
          >
            <i className={`fas fa-chevron-${expanded ? 'up' : 'down'} requirement-chevron`} aria-hidden="true" />
          </button>
        )}
      </div>
      {expanded && hasFlags && (
        <div className="requirement-flags dome-d-flex dome-flex-column dome-gap3">
          {filteredReqFlags.map((flag) => (
            <div key={flag.id} className="flag-note-container">
              <FlagItem.Content flag={flag} />
            </div>
          ))}
          {filteredDocGroups.flatMap(({ doc, flags }) => (
            <div className="dome-d-flex dome-flex-column" key={doc.id}>
              <FlagItem.FileName key={`doc-label-${doc.id}`} fileName={doc.label} />
              <div className="dome-d-flex dome-flex-column dome-gap3">
                {flags.flatMap((flag) => [
                  <div key={flag.id} className="flag-note-container">
                    <FlagItem.Content flag={flag} />
                  </div>,
                ])}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

export default function FlagsSidebar({ requirements = [], role, isOpen, onClose }) {
  const flags = useFlagStore(selectAllFlags)
  const [activeTab, setActiveTab] = useState(0)
  const [selectedRequirementId, setSelectedRequirementId] = useState(null)

  useEffect(() => {
    if (isOpen) setSelectedRequirementId(null)
  }, [isOpen])

  const reviewing = filterForReviewing(flags, role)
  const secondTabLabel = isApplicant(role) ? 'completed' : 'resolved'
  const currentTab = activeTab === 0 ? 'reviewing' : isApplicant(role) ? 'completed' : 'resolved'

  return (
    <div
      className={`flags-sidebar dome-d-flex dome-flex-column dome-gap18 dome-light-shadow dome-bg-white dome-full-height${isOpen ? ' open' : ''}`}
    >
      <div className="flags-sidebar-header dome-d-flex dome-flex-column dome-gap18 dome-relative">
        <button
          type="button"
          aria-label="close flags sidebar"
          className="flags-sidebar-close dome-cursor-pointer dome-absolute"
          onClick={onClose}
        >
          <i className="fat fa-xmark dome-p1-lt" />
        </button>
        <div className="dome-d-flex dome-flex-column dome-gap6">
          <div className="dome-d-flex dome-align-center dome-justify-between">
            <span className="flags-sidebar-title dome-h2">Flags</span>
            <span className="dome-color-orange-you-glad-im-back dome-h3 dome-d-flex dome-align-center dome-gap3">
              <span>{reviewing.length}</span>
              <i className="fas fa-flag-swallowtail dome-p3" />
            </span>
          </div>
          <div className="dome-color-med-grey">
            {currentTab === 'reviewing' ? 'Select a requirement to jump to.' : 'Review all flags for this application.'}
          </div>
        </div>

        <div className="flags-sidebar-tabs dome-d-flex">
          <button
            className={`flags-sidebar-tab dome-cursor-pointer${activeTab === 0 ? ' active' : ''}`}
            onClick={() => setActiveTab(0)}
          >
            reviewing
          </button>
          <button
            className={`flags-sidebar-tab dome-cursor-pointer${activeTab === 1 ? ' active' : ''}`}
            onClick={() => setActiveTab(1)}
          >
            {secondTabLabel}
          </button>
        </div>
      </div>
      <div className="flags-sidebar-content dome-p1 dome-scrollable">
        <ol className="flags-sidebar-list dome-d-flex dome-flex-column dome-gap12">
          {requirements?.map((req, index) => (
            <li key={req.id}>
              <RequirementRow
                requirement={req}
                index={index}
                role={role}
                tab={currentTab}
                isOpen={isOpen}
                isSelected={selectedRequirementId === req.id}
                onSelect={() => setSelectedRequirementId(req.id)}
              />
            </li>
          ))}
        </ol>
        {!requirements?.length && <div className="flags-empty dome-text-center">No requirements to show.</div>}
      </div>
    </div>
  )
}
